import { formatDateDefault, formatTime24Hour, formatTimeSpanNormal, HostContext, HostContextValue, WorkItemContext, WorkItemContextValue } from "@buzzeasy/shared-frontend-utilities";
import { Alert, message, notification } from "antd";
import { useMemo } from "react";
import appTheme from "./appTheme";
import MainContainer from "./components/containers/MainContainer";
import Providers from "./components/providers/Providers";
import environmentConfig from "./environmentConfig";
import GlobalStyle from "./GlobalStyle";
import { CallTranslatorMode } from "./model/hubDtos";

const { useMessage } = message;
const { useNotification } = notification;

const { apiBaseUrl } = environmentConfig;

const queryParameters = new URLSearchParams(window.location.search);
const conversationId = queryParameters.get("conversationId");
const modeParam = queryParameters.get("mode");
const mode: CallTranslatorMode = modeParam === "transcription" ? "transcription" : "translation";

function App() {
  const [messageApi, messageContextHolder] = useMessage();
  const [notificationApi, notificationContextHolder] = useNotification();

  const hostContextValue = useMemo<HostContextValue>(
    () => ({
      apiBaseUrl,
      token: "", // not used
      theme: appTheme.light,
      language: "en-US",
      isWindowFocused: true,
      displaySettings: {
        hideSensitiveAgentInformation: false,
        hideExternalContacts: false,
      },
      messageApi,
      notificationApi,
      formattingApi: {
        formatDate: formatDateDefault,
        formatTime: formatTime24Hour,
        formatDateTime: (date: Date) => `${formatDateDefault(date)} ${formatTime24Hour(date)}`,
        formatTimeSpan: formatTimeSpanNormal,
      },
    }),
    [messageApi, notificationApi],
  );

  const workItemContextValue = useMemo<WorkItemContextValue>(
    () => ({
      agentWorkspace: null,
      businessUnitId: null,
      channelId: null,
      caseId: null,
      conversations: [],
      createdAt: new Date(),
      customer: {
        id: "",
        identifier: "",
        phoneNumber: "",
      },
      isOutbound: true,
      isPrimaryConversationTranslated: false,
      isPredictive: false,
      lastStateChangeDate: new Date(),
      primaryChannelType: "voice",
      primaryConversationId: "",
      primaryMediaType: "Voice",
      scripting: {
        externalWebPageTitle: null,
        externalWebPageUrlTemplate: null,
        isScriptProcessCompleted: false,
        scriptProcessId: null,
        externalWebPagePresentationMode: null,
      },
      workItemId: "",
      workItemState: "connected",
      channelIdentifier: "",
      wrapUp: {
        completionCodes: [],
        durationSeconds: 0,
        forceCompletionCodeSelection: false,
        remainingResets: 0,
        copilotId: null,
        disableNotesEditing: false,
      },
    }),
    [],
  );

  if (!conversationId) {
    // eslint-disable-next-line @stylistic/jsx-one-expression-per-line
    return <Alert showIcon type="error" message={<>No <code style={{ fontWeight: "bold" }}>conversationId</code> search parameter was provided in the URL.</>} />;
  }

  return (
    <HostContext.Provider value={hostContextValue}>
      <WorkItemContext.Provider value={workItemContextValue}>
        <Providers conversationId={conversationId} mode={mode}>
          <GlobalStyle />
          <MainContainer showHeader conversationId={conversationId} />
          {messageContextHolder}
          {notificationContextHolder}
        </Providers>
      </WorkItemContext.Provider>
    </HostContext.Provider>
  );
}

export default App;
